import type { PaginatedResponse } from "@/types/rbac";
import type {
  AgentPolicyView,
  AgentSettlement,
  AgentSettlementsAggregate,
  BatchImportPreview,
  DetectionResult,
  ImportHistoryItem,
  ImportPreview,
  ImportResult,
  Insurer,
  ManualEntryRequest,
  ManualPolicyResponse,
  MonthChecklist,
  OfwcaMapping,
  OrphanedAssignment,
  PolicyOverride,
  PolicyReassignment,
  Settlement,
  SettlementApprovalPreview,
  SettlementRevision,
  SettlementsAggregate,
  SplitPolicyRequest,
  SplitPolicyResult,
} from "@/types/settlement";
import { apiClient } from "./client";

export const settlementsApi = {
  // ========================================
  // Settlements
  // ========================================

  getAllSettlements(params: {
    year?: number;
    month?: number;
    status?: string;
    agentId?: string;
    page?: number;
    pageSize?: number;
  }) {
    const search = new URLSearchParams();
    if (params.year) search.set("year", String(params.year));
    if (params.month) search.set("month", String(params.month));
    if (params.status && params.status !== "all")
      search.set("status", params.status);
    if (params.agentId) search.set("agentId", params.agentId);
    if (params.page) search.set("page", String(params.page));
    if (params.pageSize) search.set("pageSize", String(params.pageSize));
    const qs = search.toString();
    return apiClient.get<PaginatedResponse<Settlement>>(
      `/settlements${qs ? `?${qs}` : ""}`,
    );
  },

  getAgentSettlements(
    agentId: string,
    params: {
      year?: number;
      month?: number;
      status?: string;
      page?: number;
      pageSize?: number;
    },
  ) {
    const search = new URLSearchParams();
    if (params.year) search.set("year", String(params.year));
    if (params.month) search.set("month", String(params.month));
    if (params.status) search.set("status", params.status);
    if (params.page) search.set("page", String(params.page));
    if (params.pageSize) search.set("pageSize", String(params.pageSize));
    const qs = search.toString();
    return apiClient.get<PaginatedResponse<AgentSettlement>>(
      `/settlements/agent/${agentId}${qs ? `?${qs}` : ""}`,
    );
  },

  // Admin-only aggregate across the filter set. Kafelki muszą brać sumy
  // z całego wyniku, nie tylko z bieżącej strony.
  getSettlementsAggregate(params: {
    year?: number;
    month?: number;
    status?: string;
    agentId?: string;
  }) {
    const search = new URLSearchParams();
    if (params.year) search.set("year", String(params.year));
    if (params.month) search.set("month", String(params.month));
    if (params.status && params.status !== "all")
      search.set("status", params.status);
    if (params.agentId) search.set("agentId", params.agentId);
    const qs = search.toString();
    return apiClient.get<SettlementsAggregate>(
      `/settlements/aggregate${qs ? `?${qs}` : ""}`,
    );
  },

  // Agent-scoped aggregate (owner-only enforced server-side).
  // Zwraca tylko pola dozwolone dla agenta.
  getAgentSettlementsAggregate(
    agentId: string,
    params: { year?: number; month?: number; status?: string },
  ) {
    const search = new URLSearchParams();
    if (params.year) search.set("year", String(params.year));
    if (params.month) search.set("month", String(params.month));
    if (params.status && params.status !== "all")
      search.set("status", params.status);
    const qs = search.toString();
    return apiClient.get<AgentSettlementsAggregate>(
      `/settlements/agent/${agentId}/aggregate${qs ? `?${qs}` : ""}`,
    );
  },

  getAgentPolicies(
    agentId: string,
    year: number,
    month: number,
    page = 1,
    pageSize = 50,
  ) {
    return apiClient.get<AgentPolicyView>(
      `/settlements/agent/${agentId}/policies?year=${year}&month=${month}&page=${page}&pageSize=${pageSize}`,
    );
  },

  getSettlementApprovalPreview(id: string) {
    return apiClient.get<SettlementApprovalPreview>(
      `/settlements/${id}/approval-preview`,
    );
  },

  approveSettlement(id: string, policyOverrides?: PolicyReassignment[]) {
    const body = policyOverrides?.length ? { policyOverrides } : undefined;
    return apiClient.post<void>(`/settlements/${id}/approve`, body);
  },

  markSettlementPaid(id: string) {
    return apiClient.post<void>(`/settlements/${id}/pay`);
  },

  unlockSettlement(id: string, reason: string) {
    return apiClient.post<void>(`/settlements/${id}/unlock`, { reason });
  },

  getSettlementRevisions(id: string) {
    return apiClient.get<SettlementRevision[]>(`/settlements/${id}/revisions`);
  },

  async exportSettlementExcel(id: string) {
    const { blob, response } = await apiClient.getBlob(
      `/settlements/${id}/export`,
    );
    const disposition = response.headers.get("content-disposition");
    // Prefer filename* (RFC 5987, UTF-8 encoded) over plain filename
    const fileNameStarMatch = disposition?.match(
      /filename\*=UTF-8''(.+?)(?:;|$)/i,
    );
    const fileNameMatch = disposition?.match(/filename="?([^";]+)"?/);
    const rawName = fileNameStarMatch?.[1] ?? fileNameMatch?.[1];
    const fileName = rawName
      ? decodeURIComponent(rawName)
      : `rozliczenie-${id}.xlsx`;
    return { blob, fileName };
  },

  // ========================================
  // Import
  // ========================================

  previewImport(file: File, insurerId: string, year: number, month: number) {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.postFormData<ImportPreview>(
      `/imports/preview?insurerId=${insurerId}&year=${year}&month=${month}`,
      formData,
    );
  },

  confirmImport(
    importId: string,
    mappings: OfwcaMapping[],
    policyOverrides?: PolicyOverride[],
    orphanedAssignments?: OrphanedAssignment[],
  ) {
    return apiClient.post<ImportResult>("/imports/confirm", {
      importId,
      mappings,
      policyOverrides: policyOverrides ?? [],
      orphanedAssignments: orphanedAssignments ?? [],
    });
  },

  detectInsurer(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.postFormData<DetectionResult>("/imports/detect", formData);
  },

  getInsurers() {
    return apiClient.get<Insurer[]>("/imports/insurers");
  },

  getImportHistory(params: {
    page?: number;
    pageSize?: number;
    status?: string;
  }) {
    const search = new URLSearchParams();
    if (params.page) search.set("page", String(params.page));
    if (params.pageSize) search.set("pageSize", String(params.pageSize));
    if (params.status) search.set("status", params.status);
    const qs = search.toString();
    return apiClient.get<PaginatedResponse<ImportHistoryItem>>(
      `/imports/history${qs ? `?${qs}` : ""}`,
    );
  },

  deleteImport(id: string) {
    return apiClient.delete<void>(`/imports/${id}`);
  },

  batchPreviewImport(
    file: File,
    insurerIds: string[],
    year: number,
    month: number,
  ) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("insurerIds", JSON.stringify(insurerIds));
    formData.append("year", year.toString());
    formData.append("month", month.toString());
    return apiClient.postFormData<BatchImportPreview>(
      "/imports/batch-preview",
      formData,
    );
  },

  cancelImport(importId: string) {
    return apiClient.post<void>(`/imports/${importId}/cancel`, {});
  },

  getMonthChecklist(year: number, month: number) {
    return apiClient.get<MonthChecklist>(
      `/imports/month-checklist?year=${year}&month=${month}`,
    );
  },

  confirmNoCommission(data: {
    insurerId: string;
    year: number;
    month: number;
    note?: string;
  }) {
    return apiClient.post<void>("/imports/month-checklist/no-commission", data);
  },

  revokeNoCommission(insurerId: string, year: number, month: number) {
    return apiClient.delete<void>(
      `/imports/month-checklist/no-commission/${insurerId}/${year}/${month}`,
    );
  },

  // ========================================
  // Manual Entries
  // ========================================

  addManualEntry(settlementId: string, data: ManualEntryRequest) {
    return apiClient.post<ManualPolicyResponse>(
      `/settlements/${settlementId}/manual-entries`,
      data,
    );
  },

  updateManualEntry(
    settlementId: string,
    policyId: string,
    data: ManualEntryRequest,
  ) {
    return apiClient.put<ManualPolicyResponse>(
      `/settlements/${settlementId}/manual-entries/${policyId}`,
      data,
    );
  },

  deleteManualEntry(settlementId: string, policyId: string) {
    return apiClient.delete<void>(
      `/settlements/${settlementId}/manual-entries/${policyId}`,
    );
  },

  // ========================================
  // Commission Split (admin only)
  // ========================================

  splitPolicyCommission(
    settlementId: string,
    policyId: string,
    body: SplitPolicyRequest,
  ) {
    return apiClient.post<SplitPolicyResult>(
      `/settlements/${settlementId}/policies/${policyId}/split`,
      body,
    );
  },

  unsplitPolicyCommission(settlementId: string, policyId: string) {
    return apiClient.delete<void>(
      `/settlements/${settlementId}/policies/${policyId}/split`,
    );
  },
};
